import { useCallback } from 'react';
import type { KeyboardEvent, RefObject } from 'react';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ESCAPE_KEY } from 'common/constants/general';

interface UseAuthSelectorKeyboardNavigationOptions {
  inputRef?: RefObject<HTMLInputElement | null>;
  listRef: RefObject<HTMLElement | null>;
}

const useAuthSelectorKeyboardNavigation = ({
  inputRef,
  listRef,
}: UseAuthSelectorKeyboardNavigationOptions) => {
  const getOptionLinks = useCallback(() => {
    const listElement = listRef.current;

    if (!listElement) {
      return [];
    }

    return Array.from(listElement.querySelectorAll<HTMLAnchorElement>('a[href]')).filter(
      link => link.getClientRects().length > 0 && !link.hasAttribute('aria-disabled')
    );
  }, [listRef]);

  const focusLink = useCallback((link: HTMLAnchorElement | undefined) => {
    if (!link) {
      return false;
    }

    link.focus();
    link.scrollIntoView({ block: 'nearest' });
    return true;
  }, []);

  const focusFirstOption = useCallback(() => {
    const optionLinks = getOptionLinks();
    return focusLink(optionLinks[0]);
  }, [focusLink, getOptionLinks]);

  const focusLastOption = useCallback(() => {
    const optionLinks = getOptionLinks();
    return focusLink(optionLinks.at(-1));
  }, [focusLink, getOptionLinks]);

  const focusAdjacentOption = useCallback((currentLink: HTMLAnchorElement, direction: 1 | -1) => {
    const optionLinks = getOptionLinks();
    const currentIndex = optionLinks.indexOf(currentLink);

    if (currentIndex === -1) {
      return false;
    }

    return focusLink(optionLinks[currentIndex + direction]);
  }, [focusLink, getOptionLinks]);

  const handleSearchKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ARROW_DOWN_KEY) {
      if (focusFirstOption()) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === ARROW_UP_KEY && focusLastOption()) {
      event.preventDefault();
    }
  }, [focusFirstOption, focusLastOption]);

  const handleOptionsKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null;
    const currentLink = target?.closest<HTMLAnchorElement>('a[href]');

    if (!currentLink) {
      return;
    }

    if (event.key === ARROW_DOWN_KEY) {
      if (focusAdjacentOption(currentLink, 1)) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === ARROW_UP_KEY) {
      if (focusAdjacentOption(currentLink, -1)) {
        event.preventDefault();
      }
      return;
    }

    if (event.key === ESCAPE_KEY && inputRef?.current) {
      event.preventDefault();
      inputRef.current.focus();
    }
  }, [focusAdjacentOption, inputRef]);

  return {
    focusFirstOption,
    handleOptionsKeyDown,
    handleSearchKeyDown,
  };
};

export default useAuthSelectorKeyboardNavigation;
